import store from '../redux/store';
import { invalidateCacheEntries, writeCacheEntryAction } from '../redux/actions';

// Default: keep cached entries until explicitly invalidated (infinite TTL)
const DEFAULT_TTL_MS = Infinity;

const selectEntry = (key) => store.getState()?.cache?.entries?.[key];

export const isEntryFresh = (entry, ttlMs = DEFAULT_TTL_MS) => {
  if (!entry || !entry.updatedAt) return false;
  const effectiveTtl = Number.isFinite(ttlMs) ? ttlMs : entry.ttlMs || DEFAULT_TTL_MS;
  if (!Number.isFinite(effectiveTtl)) return true;
  return Date.now() - entry.updatedAt < effectiveTtl;
};

export const getCachedValue = (key, ttlMs = DEFAULT_TTL_MS) => {
  const entry = selectEntry(key);
  if (!isEntryFresh(entry, ttlMs)) return null;
  return entry.data;
};

export const setCachedValue = (key, data, ttlMs = DEFAULT_TTL_MS) => {
  store.dispatch(
    writeCacheEntryAction({
      key,
      data,
      ttlMs,
      updatedAt: Date.now(),
    }),
  );
  return data;
};

export const updateCachedValue = (key, updater, options = {}) => {
  const entry = selectEntry(key);
  if (!entry) return null;
  const nextData = typeof updater === 'function' ? updater(entry.data) : updater;
  if (nextData === undefined) return entry.data;
  const ttlMs = Number.isFinite(options.ttlMs) ? options.ttlMs : entry.ttlMs || DEFAULT_TTL_MS;
  store.dispatch(
    writeCacheEntryAction({
      key,
      data: nextData,
      ttlMs,
      updatedAt: Date.now(),
    }),
  );
  return nextData;
};

export const invalidateCache = (key, options = {}) =>
  store.dispatch(invalidateCacheEntries(key, options));

export const cachedJsonFetch = async (
  endpoint,
  {
    requestOptions,
    cacheKey,
    ttlMs = DEFAULT_TTL_MS,
    forceRefresh = false,
    fetcher,
  } = {},
) => {
  const key = cacheKey || endpoint;
  const entry = selectEntry(key);
  const effectiveTtl = Number.isFinite(ttlMs) ? ttlMs : entry?.ttlMs || DEFAULT_TTL_MS;

  if (!forceRefresh && isEntryFresh(entry, effectiveTtl)) {
    return { data: entry.data, fromCache: true, stale: false };
  }

  try {
    if (typeof fetcher !== 'function') {
      throw new Error('cachedJsonFetch requires a fetcher function');
    }
    const response = await fetcher(endpoint, requestOptions);
    const data = await response.json();
    store.dispatch(
      writeCacheEntryAction({
        key,
        data,
        ttlMs: effectiveTtl,
        updatedAt: Date.now(),
      }),
    );
    return { data, fromCache: false, stale: false };
  } catch (error) {
    if (entry) {
      return { data: entry.data, fromCache: true, stale: true, error };
    }
    throw error;
  }
};

export const buildCachedResponse = (data, { stale = false } = {}) => {
  const body = data !== undefined ? JSON.stringify(data) : '';
  const headers = new Headers({
    'Content-Type': 'application/json',
    'X-Cache': stale ? 'STALE' : 'HIT',
  });
  return new Response(body, { status: 200, statusText: stale ? 'OK (stale)' : 'OK', headers });
};

export const CACHE_DEFAULT_TTL_MS = DEFAULT_TTL_MS;
